import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Building2, MapPin, MessageCircleMore, Search, Sparkles } from "lucide-react";
import { Link } from "react-router";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge } from "@contractor-quoting/shared-ui";
import { Shell } from "../components/Shell.js";
import { readDraftPrompt } from "../project-intake.js";

interface ContractorDirectoryItem {
  slug: string;
  name: string;
  description: string;
  brandPrimary: string;
  serviceArea: string | null;
  supportedProjectTypes: string[];
}

const readable = (value: string) =>
  value.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase());

export function ContractorLookupPage() {
  const [contractors, setContractors] = useState<ContractorDirectoryItem[]>([]);
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [projectType, setProjectType] = useState("all");
  const [draftPrompt] = useState(readDraftPrompt);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    void apiFetch<{ items: ContractorDirectoryItem[] }>("/v1/public/contractors")
      .then((result) => setContractors(result.items))
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Contractors are unavailable"),
      )
      .finally(() => setLoading(false));
  }, []);

  const projectTypes = useMemo(
    () => [...new Set(contractors.flatMap((item) => item.supportedProjectTypes))].sort(),
    [contractors],
  );
  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const normalizedLocation = location.trim().toLowerCase();
    return contractors.filter((contractor) => {
      const searchable = [contractor.name, contractor.description, contractor.serviceArea ?? ""]
        .join(" ")
        .toLowerCase();
      return (
        (!normalizedQuery || searchable.includes(normalizedQuery)) &&
        (!normalizedLocation ||
          contractor.serviceArea?.toLowerCase().includes(normalizedLocation)) &&
        (projectType === "all" || contractor.supportedProjectTypes.includes(projectType))
      );
    });
  }, [contractors, location, projectType, query]);

  return (
    <Shell>
      <main className="min-h-[calc(100vh-73px)] bg-stone-50">
        <section className="border-b border-stone-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:py-16">
            <Badge className="bg-orange-100 text-orange-800">Contractor directory</Badge>
            <div className="mt-5 grid items-end gap-6 lg:grid-cols-[1fr_auto]">
              <div className="max-w-3xl">
                <h1 className="text-4xl font-black tracking-[-0.04em] text-emerald-950 sm:text-5xl">
                  Find the right contractor for your project.
                </h1>
                <p className="mt-4 text-lg leading-8 text-stone-600">
                  Choose a contractor first so Mezoniq can guide the project using their services,
                  process, and pricing.
                </p>
              </div>
              <p className="flex items-center gap-2 text-sm font-semibold text-stone-500">
                <Sparkles size={17} className="text-orange-600" /> Guided planning starts here
              </p>
            </div>

            <div className="mt-9 grid gap-3 rounded-2xl border border-stone-200 bg-stone-50 p-3 shadow-sm md:grid-cols-[1.2fr_0.9fr_0.8fr]">
              <label className="relative">
                <span className="sr-only">Search contractors</span>
                <Search
                  aria-hidden="true"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"
                  size={19}
                />
                <input
                  className="min-h-12 w-full rounded-xl border border-stone-300 bg-white pl-11 pr-4 outline-none transition focus:border-emerald-700 focus:ring-4 focus:ring-emerald-100"
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Contractor name or service"
                  value={query}
                />
              </label>
              <label className="relative">
                <span className="sr-only">Location</span>
                <MapPin
                  aria-hidden="true"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"
                  size={19}
                />
                <input
                  className="min-h-12 w-full rounded-xl border border-stone-300 bg-white pl-11 pr-4 outline-none transition focus:border-emerald-700 focus:ring-4 focus:ring-emerald-100"
                  onChange={(event) => setLocation(event.target.value)}
                  placeholder="City or service area"
                  value={location}
                />
              </label>
              <label>
                <span className="sr-only">Project type</span>
                <select
                  className="min-h-12 w-full rounded-xl border border-stone-300 bg-white px-4 outline-none transition focus:border-emerald-700 focus:ring-4 focus:ring-emerald-100"
                  onChange={(event) => setProjectType(event.target.value)}
                  value={projectType}
                >
                  <option value="all">All project types</option>
                  {projectTypes.map((type) => (
                    <option key={type} value={type}>
                      {readable(type)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {draftPrompt ? (
              <div className="mt-4 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                <MessageCircleMore className="mt-0.5 shrink-0" size={18} />
                <p>
                  <span className="font-bold">Your first message is ready:</span> “{draftPrompt}”
                </p>
              </div>
            ) : null}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
          <div className="mb-5 flex items-center justify-between gap-4">
            <h2 className="text-xl font-bold text-emerald-950">Available contractors</h2>
            {!loading && !error ? (
              <p aria-live="polite" className="text-sm text-stone-500">
                {filtered.length} {filtered.length === 1 ? "contractor" : "contractors"}
              </p>
            ) : null}
          </div>

          {error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800">
              {error}
            </div>
          ) : null}
          {loading ? (
            <div className="grid gap-4 lg:grid-cols-2" aria-label="Loading contractors">
              {[0, 1, 2, 3].map((item) => (
                <div className="h-56 animate-pulse rounded-2xl bg-stone-200" key={item} />
              ))}
            </div>
          ) : null}
          {!loading && !error && filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-6 py-14 text-center">
              <Search className="mx-auto text-stone-400" />
              <h2 className="mt-4 text-xl font-bold">No matching contractors yet</h2>
              <p className="mt-2 text-stone-600">Try a broader service or location search.</p>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            {filtered.map((contractor) => (
              <article
                className="flex flex-col rounded-2xl border border-stone-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                key={contractor.slug}
              >
                <div className="flex items-start gap-4">
                  <span
                    aria-hidden="true"
                    className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl text-xl font-black text-white"
                    style={{ backgroundColor: contractor.brandPrimary }}
                  >
                    {contractor.name.charAt(0).toUpperCase() || <Building2 />}
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold text-emerald-950">{contractor.name}</h3>
                    {contractor.serviceArea ? (
                      <p className="mt-1 flex items-center gap-1.5 text-sm text-stone-500">
                        <MapPin size={15} /> {contractor.serviceArea}
                      </p>
                    ) : null}
                  </div>
                </div>
                <p className="mt-5 flex-1 leading-7 text-stone-600">
                  {contractor.description ||
                    "Project planning and transparent starting estimates through Mezoniq."}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {contractor.supportedProjectTypes.map((type) => (
                    <Badge className="bg-stone-100 text-stone-700" key={type}>
                      {readable(type)}
                    </Badge>
                  ))}
                </div>
                <div className="mt-6 flex flex-col gap-3 border-t border-stone-100 pt-5 sm:flex-row sm:justify-end">
                  <Link
                    className="inline-flex min-h-11 items-center justify-center rounded-xl border border-stone-300 px-4 font-semibold text-stone-700 transition hover:border-emerald-700 hover:text-emerald-800"
                    to={`/c/${contractor.slug}`}
                  >
                    View profile
                  </Link>
                  <Link
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-emerald-800 px-4 font-semibold text-white transition hover:bg-emerald-900"
                    to={`/chat/${contractor.slug}`}
                  >
                    Start a project <ArrowRight size={17} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
    </Shell>
  );
}
